import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SkippedEmailRecord } from '../../types/gmail';
import {
  analyzeSkippedEmail,
  dismissSkippedEmail,
  getSkippedEmails,
  tagSkippedEmailSender,
} from '../../services/gmailApi';
import { COLD_OUTREACH_LABEL } from '../../constants/contactTags';
import { Button } from '../ui/Button';
import { SettingsList, SettingsListItem, SettingsSection } from '../settings/ui';
import styles from './SkippedEmailReview.module.css';

export interface SkippedEmailReviewProps {
  title?: string;
  description?: string;
  emptyMessage?: string;
}

interface SenderGroup {
  key: string;
  senderLabel: string;
  senderSecondaryLabel: string | null;
  senderValue: string | null;
  senderTags: string[];
  isColdOutreachSender: boolean;
  records: SkippedEmailRecord[];
}

const SKIPPED_EMAIL_BATCH_SIZE = 50;

const getApiErrorMessage = (err: unknown, fallback: string): string => {
  const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
  return message || fallback;
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleString();
};

const formatConfidence = (confidence: number | null): string => {
  if (confidence === null || Number.isNaN(confidence)) {
    return '—';
  }
  return `${Math.round(confidence * 100)}%`;
};

const getRecordTimestamp = (record: SkippedEmailRecord): number => {
  const timestamp = record.processedAt || record.createdAt;
  return Date.parse(timestamp || '') || 0;
};

const getSenderGroupKey = (record: SkippedEmailRecord): string => {
  return record.senderEmailAddress?.toLowerCase()
    || record.fromEmail?.trim().toLowerCase()
    || `unknown:${record.id}`;
};

const getSenderLabel = (record: SkippedEmailRecord): string => {
  return record.senderDisplayName
    || record.senderEmailAddress
    || record.fromEmail
    || 'Unknown sender';
};

const getSenderSecondaryLabel = (record: SkippedEmailRecord): string | null => {
  if (!record.senderDisplayName || !record.senderEmailAddress) {
    return null;
  }

  return record.senderEmailAddress;
};

const getSenderActionValue = (record: SkippedEmailRecord): string | null => {
  return record.fromEmail || record.senderEmailAddress;
};

const formatEmailCount = (count: number): string => {
  return `${count} email${count === 1 ? '' : 's'}`;
};

export const SkippedEmailReview: React.FC<SkippedEmailReviewProps> = ({
  title = 'Skipped Emails',
  description = 'Inspect emails triage skipped before deep analysis and override the decision when needed.',
  emptyMessage = 'No skipped emails awaiting review',
}) => {
  const [skippedEmails, setSkippedEmails] = useState<SkippedEmailRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [expandedSenders, setExpandedSenders] = useState<Record<string, boolean>>({});

  const fetchSkippedEmails = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);

      const loadedItems: SkippedEmailRecord[] = [];
      let offset = 0;
      let total = 0;

      do {
        const response = await getSkippedEmails({
          limit: SKIPPED_EMAIL_BATCH_SIZE,
          offset,
        });

        total = response.total;
        if (response.items.length === 0) {
          break;
        }

        loadedItems.push(...response.items);
        offset += response.items.length;
      } while (loadedItems.length < total && offset > 0);

      setSkippedEmails(loadedItems);
      setLoadError(null);
    } catch (err) {
      console.error('Error fetching skipped emails:', err);
      setLoadError('Failed to load skipped emails');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchSkippedEmails();
  }, [fetchSkippedEmails]);

  const removeRecordFromQueue = useCallback((recordId: string) => {
    setSkippedEmails((currentRecords) => currentRecords.filter((record) => record.id !== recordId));
  }, []);

  const removeSenderFromQueue = useCallback((senderKey: string) => {
    setSkippedEmails((currentRecords) => currentRecords.filter((record) => getSenderGroupKey(record) !== senderKey));
    setExpandedSenders((currentExpandedSenders) => {
      if (!(senderKey in currentExpandedSenders)) {
        return currentExpandedSenders;
      }

      const nextExpandedSenders = { ...currentExpandedSenders };
      delete nextExpandedSenders[senderKey];
      return nextExpandedSenders;
    });
  }, []);

  const handleAnalyzeAnyway = async (recordId: string) => {
    try {
      setActionError(null);
      setActionLoading(`analyze:${recordId}`);
      await analyzeSkippedEmail(recordId);
      removeRecordFromQueue(recordId);
    } catch (err) {
      setActionError(getApiErrorMessage(err, 'Failed to queue email for analysis'));
      console.error('Error analyzing skipped email:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismissSkipped = async (recordId: string) => {
    try {
      setActionError(null);
      setActionLoading(`dismiss:${recordId}`);
      await dismissSkippedEmail(recordId);
      removeRecordFromQueue(recordId);
    } catch (err) {
      setActionError(getApiErrorMessage(err, 'Failed to dismiss skipped email'));
      console.error('Error dismissing skipped email:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleTagSender = async (senderGroup: SenderGroup) => {
    if (!senderGroup.senderValue) {
      return;
    }

    try {
      setActionError(null);
      setActionLoading(`tag:${senderGroup.key}`);
      await tagSkippedEmailSender(senderGroup.senderValue);
      removeSenderFromQueue(senderGroup.key);
    } catch (err) {
      setActionError(getApiErrorMessage(err, `Failed to tag sender as ${COLD_OUTREACH_LABEL}`));
      console.error('Error tagging skipped email sender:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const senderGroups = useMemo(() => {
    const groups = new Map<string, SenderGroup>();

    skippedEmails.forEach((record) => {
      const key = getSenderGroupKey(record);
      const existingGroup = groups.get(key);

      if (existingGroup) {
        existingGroup.records.push(record);
        existingGroup.senderTags = existingGroup.senderTags.length > 0
          ? existingGroup.senderTags
          : record.senderTags;
        existingGroup.isColdOutreachSender = existingGroup.isColdOutreachSender || record.isColdOutreachSender;
        return;
      }

      groups.set(key, {
        key,
        senderLabel: getSenderLabel(record),
        senderSecondaryLabel: getSenderSecondaryLabel(record),
        senderValue: getSenderActionValue(record),
        senderTags: record.senderTags,
        isColdOutreachSender: record.isColdOutreachSender,
        records: [record],
      });
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        records: [...group.records].sort((leftRecord, rightRecord) => (
          getRecordTimestamp(rightRecord) - getRecordTimestamp(leftRecord)
        )),
      }))
      .sort((leftGroup, rightGroup) => (
        getRecordTimestamp(rightGroup.records[0]) - getRecordTimestamp(leftGroup.records[0])
      ));
  }, [skippedEmails]);

  const hiddenSenderCount = senderGroups.filter((group) => group.isColdOutreachSender).length;
  const visibleSenderGroups = senderGroups.filter((group) => !group.isColdOutreachSender);
  const resolvedEmptyMessage = hiddenSenderCount > 0
    ? `All skipped senders are hidden because they are tagged ${COLD_OUTREACH_LABEL}`
    : emptyMessage;

  return (
    <SettingsSection title={title} description={description}>
      {actionError && (
        <div className={styles.error}>
          {actionError}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActionError(null)}
            aria-label="Dismiss skipped email action error"
          >
            Dismiss
          </Button>
        </div>
      )}

      {loadError && (
        <div className={styles.error}>
          {loadError}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void fetchSkippedEmails();
            }}
            aria-label="Retry loading skipped emails"
          >
            Retry
          </Button>
        </div>
      )}

      {loading && skippedEmails.length === 0 ? (
        <div className={styles.loading}>Loading skipped emails...</div>
      ) : (!loadError || skippedEmails.length > 0) && (
        <>
          {hiddenSenderCount > 0 && (
            <div className={styles.hiddenSummary}>
              {hiddenSenderCount} sender group{hiddenSenderCount === 1 ? '' : 's'} hidden because tagged {COLD_OUTREACH_LABEL}.
            </div>
          )}

          <SettingsList emptyMessage={resolvedEmptyMessage}>
            {visibleSenderGroups.map((senderGroup) => {
              const latestRecord = senderGroup.records[0];
              const isExpanded = expandedSenders[senderGroup.key] ?? false;

              return (
                <SettingsListItem key={senderGroup.key} className={`${styles.skippedEmailItem} ${styles.senderGroupItem}`}>
                  <div className={styles.senderGroupHeader}>
                    <div className={styles.senderGroupContent}>
                      <div className={styles.senderHeading}>
                        <span className={styles.skippedEmailSender}>{senderGroup.senderLabel}</span>
                        {senderGroup.senderSecondaryLabel && (
                          <span className={styles.senderEmailAddress}>{senderGroup.senderSecondaryLabel}</span>
                        )}
                        {senderGroup.senderTags.map((tagName) => (
                          <span key={tagName} className={styles.senderTag}>
                            {tagName}
                          </span>
                        ))}
                      </div>

                      <div className={styles.skippedEmailMeta}>
                        <span>{formatEmailCount(senderGroup.records.length)}</span>
                        <span>Latest subject: {latestRecord.subject || '(No subject)'}</span>
                        <span>Reason: {latestRecord.skipReason || latestRecord.reasonCode || 'No reason recorded'}</span>
                        <span>Skipped: {formatDate(latestRecord.processedAt || latestRecord.createdAt)}</span>
                      </div>
                    </div>

                    <div className={styles.senderGroupActions}>
                      {senderGroup.senderValue && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleTagSender(senderGroup)}
                          loading={actionLoading === `tag:${senderGroup.key}`}
                          disabled={actionLoading !== null}
                        >
                          Mark {COLD_OUTREACH_LABEL}
                        </Button>
                      )}

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedSenders((currentExpandedSenders) => ({
                          ...currentExpandedSenders,
                          [senderGroup.key]: !isExpanded,
                        }))}
                        disabled={actionLoading !== null}
                      >
                        {isExpanded ? 'Hide emails' : `Show emails (${senderGroup.records.length})`}
                      </Button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className={styles.senderGroupEmails}>
                      {senderGroup.records.map((record) => (
                        <div key={record.id} className={styles.skippedEmailCard}>
                          <div className={styles.skippedEmailContent}>
                            <div className={styles.skippedEmailHeader}>
                              <span className={styles.skippedEmailSubject}>{record.subject || '(No subject)'}</span>
                            </div>
                            <div className={styles.skippedEmailMeta}>
                              <span>Account: {record.accountEmail || 'Unknown account'}</span>
                              <span>Reason: {record.skipReason || record.reasonCode || 'No reason recorded'}</span>
                              <span>Confidence: {formatConfidence(record.triageConfidence)}</span>
                              <span>Skipped: {formatDate(record.processedAt || record.createdAt)}</span>
                            </div>
                          </div>

                          <div className={styles.skippedEmailActions}>
                            {record.gmailUrl && (
                              <a
                                className={styles.gmailLink}
                                href={record.gmailUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Open in Gmail
                              </a>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleAnalyzeAnyway(record.id)}
                              loading={actionLoading === `analyze:${record.id}`}
                              disabled={actionLoading !== null}
                            >
                              Analyze anyway
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDismissSkipped(record.id)}
                              loading={actionLoading === `dismiss:${record.id}`}
                              disabled={actionLoading !== null}
                            >
                              Dismiss
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </SettingsListItem>
              );
            })}
          </SettingsList>
        </>
      )}
    </SettingsSection>
  );
};

export default SkippedEmailReview;
